import { cn } from "@-/com.npmjs.cn";
import { View } from "@-/com.npmjs.react-native";
import {
  createAnimatedComponent,
  ReduceMotion,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { type ReactNode, useEffect } from "react";

const AnimatedView = createAnimatedComponent(View);
const FILL_TIMING = { duration: 350, reduceMotion: ReduceMotion.System };

/** The layer an ink copy sits over: black reads on every fill color, theme ink on the track. */
export type ProgressMeterInk = "fill" | "track";

/**
 * A flat pill drawn like a card: a surface track inside the card's border, with a solid fill that
 * grows from the start (from the bottom when vertical). Sliding a full-length fill keeps its
 * rounded edge without per-frame layout. It is the review recharge bar's meter, and the
 * onboarding's progress bar.
 *
 * `children` draws the bar's ink twice: theme ink over the track, and black ink clipped to the
 * fill, so a stroke stays readable wherever the fill's edge crosses it. A faded fill is too faint
 * for black ink, so it only gets the theme copy, drawn above it.
 */
export function ProgressMeter({
  children,
  fillColor,
  fillTestID = "progress-meter-fill",
  fraction,
  isFaded = false,
  testID = "progress-meter",
  vertical = false,
}: {
  children?: (ink: ProgressMeterInk) => ReactNode;
  /** The fill's color (see `getProgressMeterFillColor`), animated when it changes. */
  fillColor: string;
  fillTestID?: string;
  /** 0 → 1. */
  fraction: number;
  isFaded?: boolean;
  testID?: string;
  vertical?: boolean;
}) {
  const clampedFraction = Math.max(0, Math.min(1, fraction));
  // Shared values, not render-time closures: a worklet's captured JS values can go stale on native.
  const progress = useSharedValue(clampedFraction);
  const color = useSharedValue(fillColor);
  const isVertical = useSharedValue(vertical);
  useEffect(() => {
    progress.set(withTiming(clampedFraction, FILL_TIMING));
    color.set(withTiming(fillColor, FILL_TIMING));
    isVertical.set(vertical);
  }, [clampedFraction, color, fillColor, isVertical, progress, vertical]);
  const fillStyle = useAnimatedStyle(() => {
    const empty = (1 - progress.get()) * 100;
    return {
      backgroundColor: color.get(),
      transform: [isVertical.get() ? { translateY: `${empty}%` } : { translateX: `${-empty}%` }],
    };
  });
  // Cancels the fill's slide, so the black ink stays put while the fill's edge clips it.
  const fillInkStyle = useAnimatedStyle(() => {
    const empty = (1 - progress.get()) * 100;
    return {
      transform: [isVertical.get() ? { translateY: `${-empty}%` } : { translateX: `${empty}%` }],
    };
  });
  const trackInk = children?.("track");

  return (
    <View
      accessibilityElementsHidden
      className="size-full overflow-hidden rounded-full bg-surface"
      importantForAccessibility="no-hide-descendants"
      pointerEvents="none"
      testID={testID}
    >
      {isFaded ? null : trackInk}
      <AnimatedView
        className={cn("absolute inset-0 overflow-hidden rounded-full", isFaded && "opacity-35")}
        style={fillStyle}
        testID={fillTestID}
      >
        {isFaded || !children ? null : (
          <AnimatedView className="absolute inset-0" style={fillInkStyle}>
            {children("fill")}
          </AnimatedView>
        )}
      </AnimatedView>
      {isFaded ? trackInk : null}
      {/* On top of the fill, so the full bar keeps the same outline as the empty one. */}
      <View className="absolute inset-0 rounded-full border border-border" />
    </View>
  );
}
